/**
 * AI 가 SP를 직접 부를 때 쓰는 실행기
 *
 * 이 ERP의 모든 SP는 게이트웨이 한 곳만 지나간다:
 *   POST /ERP/GetDataSet?procedureName=DBO.XXX   body: { Table1: [{ 컬럼명, 값 }] }
 * 그래서 화면마다 함수를 하나씩 붙여줄 필요 없이, 여기 한 곳에서 아무 SP나 부를 수 있다.
 *
 * 대신 "아무거나 부를 수 있다"는 건 위험하기도 하므로 안전장치를 둔다:
 *   1) 카탈로그에 있는 SP만 허용한다 (코드가 실제로 쓰는 232개).
 *   2) 저장/수정/삭제(_T_)는 allowWrite 를 명시적으로 켜야만 실행된다. (UI에서 사용자가 확인 버튼을 눌렀을 때만)
 *   3) 파라미터 이름이 카탈로그와 다르면 실행하지 않고, 올바른 이름 목록을 돌려준다 → AI가 스스로 고쳐서 다시 부른다.
 *   4) 업체번호/유저번호처럼 로그인 정보에서 나오는 값은 AI가 지어내지 못하게 항상 우리가 채운다.
 *
 * 데모 모드에서는 demoApi.js 가 fetch 를 가로채므로 여기 코드를 고치지 않아도 가짜 데이터로 응답된다.
 */

import { getAuthHeader, getUserInfo } from '../../utils/tokenManager';
import { API_URL } from '../../config/apiConfig';
import { findProcedure } from './spCatalog';

/** AI 에게 돌려줄 최대 행 수 — 너무 많으면 답변이 느려지고 비용도 커진다 */
const MAX_ROWS = 30;
/** 한 셀에 들어가는 글자 수 상한 (본문 HTML 같은 게 통째로 들어오는 걸 막는다) */
const MAX_CELL_CHARS = 300;

export class SpCallError extends Error {
  constructor(message, detail) {
    super(message);
    this.name = 'SpCallError';
    this.detail = detail || null;
  }
}

/** [{name, value}] 또는 {name: value} 를 {'@업체번호': '1'} 형태로 통일 */
function toParamMap(params) {
  if (!params) return {};
  if (Array.isArray(params)) {
    const map = {};
    for (const p of params) {
      if (!p) continue;
      const name = String(p.name ?? p.이름 ?? '').trim();
      if (!name) continue;
      map[name.startsWith('@') ? name : `@${name}`] = p.value ?? p.값 ?? '';
    }
    return map;
  }
  const map = {};
  for (const [k, v] of Object.entries(params)) {
    map[k.startsWith('@') ? k : `@${k}`] = v;
  }
  return map;
}

/** 카탈로그의 타입 힌트대로 값을 맞춘다 */
function castValue(raw, type) {
  if (raw === null || raw === undefined) return type === 'number' ? 0 : '';
  if (type === 'number') {
    const n = Number(String(raw).replace(/,/g, ''));
    return Number.isFinite(n) ? n : 0;
  }
  return String(raw);
}

/** 응답에서 결과 테이블들을 꺼낸다 (서버가 table / Table / table1 ... 로 섞어서 준다) */
function extractTables(data) {
  const tables = {};
  if (!data || typeof data !== 'object') return tables;
  for (const [key, value] of Object.entries(data)) {
    if (/^table\d*$/i.test(key) && Array.isArray(value)) {
      tables[key.toLowerCase()] = value;
    }
  }
  return tables;
}

/** 긴 값은 자르고, 행 수도 제한해서 AI 에게 넘길 만한 크기로 만든다 */
function summarizeRows(rows) {
  const total = rows.length;
  const clipped = rows.slice(0, MAX_ROWS).map((row) => {
    if (!row || typeof row !== 'object') return row;
    const out = {};
    for (const [k, v] of Object.entries(row)) {
      if (v === null || v === undefined || v === '') continue; // 빈 칸은 빼서 응답을 가볍게
      if (typeof v === 'string' && v.length > MAX_CELL_CHARS) {
        out[k] = v.slice(0, MAX_CELL_CHARS) + '…(생략)';
      } else {
        out[k] = v;
      }
    }
    return out;
  });
  return { total, rows: clipped, truncated: total > MAX_ROWS };
}

/**
 * SP 실행.
 * @param {string} procedureName  DBO.TRP_S_상담정보 (DBO. 생략 가능)
 * @param {object|array} params   {'@상담번호': 123} 또는 [{name:'@상담번호', value:123}]
 * @param {object} options        { allowWrite }
 * @returns {Promise<{procedureName, rowCount, rows, tables, truncated}>}
 */
export async function runProcedure(procedureName, params, { allowWrite = false } = {}) {
  const entry = findProcedure(procedureName);
  if (!entry) {
    throw new SpCallError(
      `'${procedureName}' 은(는) 이 시스템에서 사용하는 SP 목록에 없습니다. search_procedures 로 정확한 이름을 먼저 찾으세요.`
    );
  }
  if (entry.kind === 'T' && !allowWrite) {
    throw new SpCallError(`'${entry.sp}' 는 데이터를 바꾸는 SP 입니다. 사용자 확인 없이 실행할 수 없습니다.`);
  }

  const provided = toParamMap(params);
  const known = new Set((entry.params || []).map((p) => p.name));
  const unknown = Object.keys(provided).filter((k) => !known.has(k));
  if (unknown.length && known.size) {
    throw new SpCallError(
      `${entry.sp} 에 없는 파라미터: ${unknown.join(', ')}. 사용 가능한 파라미터: ${[...known].join(', ')}`,
      { validParams: [...known] }
    );
  }

  const { companyNo, userNo } = getUserInfo() || {};
  const table1 = (entry.params || []).map((p) => {
    let value = provided[p.name];
    if (p.auto === 'companyNo') value = companyNo ?? value ?? '';
    else if (p.auto === 'userNo') value = userNo ?? value ?? '';
    else if (value === undefined) value = p.sample !== undefined ? p.sample : '';
    return { 컬럼명: p.name, 값: castValue(value, p.type) };
  });

  const url = `${API_URL}?procedureName=${encodeURIComponent(entry.sp)}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
    body: JSON.stringify({ Table1: table1 }),
  });

  if (!response.ok) {
    let serverMessage = '';
    try {
      serverMessage = await response.text();
    } catch (_) {}
    throw new SpCallError(`서버 오류 HTTP ${response.status}${serverMessage ? ` - ${serverMessage.slice(0, 300)}` : ''}`);
  }

  const data = await response.json();
  const tables = extractTables(data);
  const primary = tables.table || tables.table1 || [];
  const summary = summarizeRows(Array.isArray(primary) ? primary : [primary]);

  // 결과 테이블이 여러 개면 나머지는 행 수만 알려준다 (필요하면 AI가 다시 물어본다)
  const otherTables = Object.entries(tables)
    .filter(([key]) => key !== (tables.table ? 'table' : 'table1'))
    .map(([key, value]) => ({ 이름: key, 행수: Array.isArray(value) ? value.length : 1 }));

  return {
    procedureName: entry.sp,
    보낸파라미터: table1.map((t) => `${t.컬럼명}=${t.값}`).join(', '),
    rowCount: summary.total,
    rows: summary.rows,
    truncated: summary.truncated,
    ...(otherTables.length ? { 다른결과테이블: otherTables } : {}),
  };
}

/** 사용자에게 보여줄 확인 문구용 — 실제 서버로 보낼 파라미터를 미리 계산한다 */
export function previewProcedureCall(procedureName, params) {
  const entry = findProcedure(procedureName);
  if (!entry) return null;
  const provided = toParamMap(params);
  const { companyNo, userNo } = getUserInfo() || {};
  return {
    sp: entry.sp,
    kind: entry.kind,
    desc: entry.desc || '',
    params: (entry.params || []).map((p) => {
      let value = provided[p.name];
      if (p.auto === 'companyNo') value = companyNo ?? '';
      else if (p.auto === 'userNo') value = userNo ?? '';
      else if (value === undefined) value = p.sample !== undefined ? p.sample : '';
      return { name: p.name, value: castValue(value, p.type), auto: !!p.auto };
    }),
  };
}
